// ============================================================================
// TERMO REVOGACAO CLIENTE DATA — Termo de Revogação de Mandato
// Instrumento pelo qual o cliente revoga o mandato outorgado ao advogado/
// escritório. Diferente do Termo de Renúncia (ato do advogado) — ver
// TermoRenunciaAdvogadoData.js. Documentos separados por decisão do titular.
// Ver CONSTITUICAO_REGRAS.SH, artigos sobre a família de agentes de revisão
// jurídica (Art. 369-373) e o plano de implementação aprovado em 15/08/2026.
//
// ENXUTO DE PROPÓSITO (27/08/2026, Art. 389 do CONSTITUICAO_REGRAS.SH):
// contém apenas qualificação das partes (Cláusula 1ª), objeto/escopo do
// mandato revogado (Cláusula 2ª, Art. 390 — mesmo padrão "[[OBJETO]]" da
// Procuração), o ato de revogação em si (Cláusula 3ª) e data/efeitos
// (Cláusula 4ª). Cláusulas de "Honorários e Ausência de Multa" e "Entrega
// de Documentos e Prestação de Contas" foram REMOVIDAS — pesquisa de
// doutrina/prática forense confirmou que esse instrumento (termo de
// revogação) não é o lugar padrão para essas cláusulas na prática
// brasileira; elas pertencem ao Contrato de Prestação de Serviços
// Advocatícios (contrato de honorários), separado. Verificado que a [CLIENTE_E]
// (caso que motivou a revisão) não tem contrato formal registrado no
// Portal — decisão de remover, não de reduzir/manter versão enxuta, foi
// confirmada pelo titular. Se algum dia for necessário um termo
// "autônomo" (sem contrato de honorários prévio cobrindo o encerramento),
// reavaliar antes de reintroduzir essas cláusulas aqui.
//
// Base legal (pesquisada e verificada em fonte oficial/quase-oficial em
// 15/08/2026 — ver histórico de sessão):
//   art. 133 da Constituição Federal de 1988
//   art. 682, inciso I, da Lei n. 10.406/2002 (Código Civil)
//   art. 17 do Código de Ética e Disciplina da OAB (Resolução n. 02/2015 CFOAB)
//   (REsp n. 1.632.766/SP, STJ — honorários proporcionais/sem multa — não é
//   mais citado no corpo do documento após a remoção da Cláusula de
//   Honorários acima, mas permanece útil como referência de contexto caso
//   o assunto volte a ser discutido no futuro)
//
// GÊNERO DO TERMO INSTITUCIONAL ("a parte REVOGANTE"/"MANDATÁRIA") — FIXO,
// NÃO VARIÁVEL, mesmo com múltiplos REVOGANTES (ver Art. 391 abaixo). Ver
// CONSTITUICAO_REGRAS.SH Art. 381 para a análise completa. A concordância
// verbal/nominal do resto do documento continua singular-feminino
// invariável independentemente de quantos clientes reais estejam por trás
// de "a parte REVOGANTE" — só o RÓTULO ("PARTE REVOGANTE" vs. "PARTES
// REVOGANTES", `{{revoganteLabel}}`) e a lista de qualificações
// (`{{revoganteQual}}`) mudam com a quantidade.
//
// MULTI-PARTE (27/08/2026, Art. 391 do CONSTITUICAO_REGRAS.SH): `clientsData`
// é um ARRAY de clientes (era `clientData`, objeto único, até esta data) —
// caso real que motivou a mudança: 2 animais co-outorgantes ([PET_A] e
// [PET_B]) representados pela mesma tutora. Mesmo padrão de junção
// gramatical (`joinPartiesPt`) copiado do Contrato (`ContractEngine.jsx`),
// não importado cruzado — ver Art. 350. A parte MANDATÁRIA (escritório)
// continua sempre singular, não precisou do mesmo tratamento.
// ============================================================================

import { buildCanonicalClientQualification } from "../../config/ClientSchema";
import { escapeHtml } from "../../utils/escapeHtml";

export const TERMO_REVOGACAO_STRUCTURE = [
  {
    id: "revogacao_preambulo",
    title: "PREÂMBULO E DIRETRIZES DE INTERPRETAÇÃO",
    type: "text",
    tags: ["Universal"],
    condition: "ALWAYS",
    icon: "fa-gavel",
    content:
      "<p>Considerando que a advocacia é função essencial à administração da justiça, nos termos do art. 133 da Constituição Federal de 1988;</p><p>Considerando que o mandato pode ser revogado pelo mandante a qualquer tempo, independentemente de justificativa, nos termos do art. 682, inciso I, da Lei n. 10.406/2002 (Código Civil);</p><p>Considerando que a revogação do mandato judicial por vontade do cliente não o desobriga do pagamento das verbas honorárias contratadas, nos termos do art. 17 do Código de Ética e Disciplina da OAB (Resolução n. 02/2015 do Conselho Federal da OAB);</p><p>As partes qualificadas na <strong>Cláusula 1ª</strong> firmam o presente Termo de Revogação de Mandato.</p><p>Para fins de padronização e fluidez de leitura, este instrumento adotará a expressão institucional \"a parte <strong>REVOGANTE</strong>\" para referir-se ao cliente que revoga o mandato, e \"a parte <strong>MANDATÁRIA</strong>\" para referir-se ao advogado ou sociedade de advocacia cujo mandato ora se revoga — designações genéricas e invariáveis, independentemente do gênero ou natureza das partes. Toda a concordância verbal e nominal das cláusulas a seguir obedecerá estritamente a esta regra (singular e feminino).</p><p>Ressalva-se que a padronização de gênero gramatical aqui adotada tem finalidade exclusivamente técnica de uniformização redacional, não refletindo qualquer declaração sobre o gênero ou natureza das partes, assegurando-se a validade do instrumento independentemente de eventuais usos gramaticais divergentes da concordância tradicional.</p>",
  },
  {
    id: "revogacao_cl01_partes",
    title: "Das Partes",
    type: "clause",
    tags: ["Universal"],
    condition: "ALWAYS",
    icon: "fa-user",
    children: [
      {
        id: "revogacao_cl01_item1",
        type: "sub_clause",
        content: "<strong>{{revoganteLabel}}:</strong> {{revoganteQual}}",
        condition: "ALWAYS",
        level: 0,
      },
      {
        id: "revogacao_cl01_item2",
        type: "sub_clause",
        content:
          "<strong>PARTE MANDATÁRIA:</strong> {{advQual}}",
        condition: "ALWAYS",
        level: 0,
      },
    ],
  },
  {
    id: "revogacao_cl02_objeto",
    title: "Do Objeto do Mandato Revogado",
    type: "clause",
    tags: ["Universal"],
    condition: "ALWAYS",
    icon: "fa-scroll",
    children: [
      {
        id: "revogacao_cl02_item1",
        type: "sub_clause",
        content:
          "O mandato ora revogado foi outorgado pela parte <strong>REVOGANTE</strong> à parte <strong>MANDATÁRIA</strong> para a representação técnica e defesa de seus interesses atrelados ao seguinte escopo: <strong>{{objetoMandato}}</strong>, referente a {{processoRef}}.",
        condition: "ALWAYS",
        level: 0,
      },
    ],
  },
  {
    id: "revogacao_cl03_ato",
    title: "Do Ato de Revogação",
    type: "clause",
    tags: ["Universal"],
    condition: "ALWAYS",
    icon: "fa-file-signature",
    children: [
      {
        id: "revogacao_cl03_item1",
        type: "sub_clause",
        content:
          "A parte <strong>REVOGANTE</strong> revoga, nesta data, a qualquer tempo e independentemente de justificativa, o mandato descrito na <strong>Cláusula 2ª</strong>, outorgado à parte <strong>MANDATÁRIA</strong>, nos termos do art. 682, inciso I, da Lei n. 10.406/2002 (Código Civil).",
        condition: "ALWAYS",
        level: 0,
      },
    ],
  },
  {
    id: "revogacao_cl05_efeitos",
    title: "Da Data de Efeitos",
    type: "clause",
    tags: ["Universal"],
    condition: "ALWAYS",
    icon: "fa-calendar-check",
    children: [
      {
        id: "revogacao_cl05_item1",
        type: "sub_clause",
        content:
          "A presente revogação produz efeitos a partir da data de assinatura ou notificação deste instrumento, cabendo à parte <strong>REVOGANTE</strong> a imediata constituição de novo patrono, quando aplicável, para evitar prejuízo aos seus interesses em juízo.",
        condition: "ALWAYS",
        level: 0,
      },
      {
        id: "revogacao_cl05_item2",
        type: "sub_clause",
        content:
          "A revogação não exige forma específica, produzindo efeitos desde que a vontade da parte <strong>REVOGANTE</strong> nesse sentido seja comunicada de forma inequívoca à parte <strong>MANDATÁRIA</strong>, por qualquer meio idôneo — inclusive verbal, mensagem eletrônica ou aplicativo de mensagens instantâneas —, independentemente da assinatura do presente instrumento, que tem caráter declaratório e documental, destinado ao registro formal do ato e, quando cabível, à sua juntada aos autos para ciência do Juízo.",
        condition: "ALWAYS",
        level: 0,
      },
    ],
  },
];

// ── Helpers para placeholders ────────────────────────────────────────────────
// Junta N qualificações em português com a mesma regra do Contrato
// (ContractEngine.jsx, joinPartiesPt): 1 item → o próprio; 2 itens → "A e
// B"; 3+ → "A; B; e C" (ponto-e-vírgula entre os itens, "e" antes do
// último — vírgula sozinha ficaria ambígua porque cada qualificação já usa
// vírgulas internamente). Copiado, não importado cruzado (Art. 350 —
// documentos independentes não compartilham lógica de baixo nível).
const joinPartiesPt = (parts) => {
  const arr = (Array.isArray(parts) ? parts : []).filter(
    (x) => String(x || "").trim() !== "",
  );
  if (arr.length <= 1) return arr[0] || "";
  if (arr.length === 2) return `${arr[0]} e ${arr[1]}`;
  return `${arr.slice(0, -1).join("; ")}; e ${arr[arr.length - 1]}`;
};

const stripFinalPunct = (s) =>
  String(s || "").trim().replace(/[\s.,;]+$/g, "");

export function buildRevogacaoPlaceholderVars({ clientsData, officeData, processoRef, objetoMandato }) {
  const clients = (Array.isArray(clientsData) ? clientsData : []).filter(Boolean);
  const isPlaceholder = clients.length === 0;

  // Qualificação canônica do escritório/advogado (mesma fonte que Contrato/
  // Procuração usam para CONTRATADA/OUTORGADA — Art. 383, CONSTITUICAO_
  // REGRAS.SH). isAttorneyOfRecord:true ativa "...e com escritório
  // profissional..." e a cláusula de citação/intimação no endereço
  // profissional, que é o texto correto aqui (a MANDATÁRIA é o próprio
  // advogado/escritório, não um cliente comum).
  let advQual = "...";
  if (officeData) {
    advQual = String(
      buildCanonicalClientQualification(officeData, {}, { isAttorneyOfRecord: true })?.text || "",
    ).trim();
  }

  // Qualificação canônica — mesma fonte que advQual acima e que Contrato/
  // Procuração usam (buildCanonicalClientQualification), agora suportando
  // N REVOGANTES (Art. 391, CONSTITUICAO_REGRAS.SH — caso real: 2 animais
  // co-outorgantes/[PET_A] e [PET_B], representados pela mesma tutora). Cada
  // qualificação individual já vem com ponto final; removido antes de
  // juntar (joinPartiesPt) e reinserido uma única vez no final do texto
  // combinado — não é o mesmo "stripar e nunca reinserir" que causava a
  // gambiarra do Art. 387/388 (lá a captura tinha 1 só qualificação e o
  // ponto nunca voltava; aqui reinserir é estruturalmente necessário para
  // combinar N textos em 1 frase). "condição de saúde declarada" é
  // removida por ser dado administrativo interno, antes do join.
  let revoganteQual = "...";
  let revoganteLabel = "PARTE REVOGANTE";
  if (!isPlaceholder) {
    const rawParts = clients.map((c) => {
      const raw = String(
        buildCanonicalClientQualification(c, {})?.text || "",
      ).trim();
      const cleaned = raw.replace(
        /,?\s*condição de saúde declarada:\s*[^,.]+/gi,
        "",
      );
      return stripFinalPunct(cleaned);
    });
    const joined = joinPartiesPt(rawParts);
    revoganteQual = joined ? `${joined}.` : "...";
    revoganteLabel = clients.length > 1 ? "PARTES REVOGANTES" : "PARTE REVOGANTE";
  }

  return {
    "{{advQual}}": advQual,
    "{{revoganteQual}}": revoganteQual,
    "{{revoganteLabel}}": revoganteLabel,
    "{{processoRef}}": String(processoRef || "[processo/causa a especificar]").trim(),
    "{{objetoMandato}}": String(objetoMandato || "[objeto do mandato a especificar]").trim(),
  };
}

// Chaves cujo valor já é HTML pré-montado e seguro — mesmo padrão de
// proteção contra XSS já aplicado em Declaração/Procuração/Contrato
// (Art. 341/343).
const HTML_TRUSTED_VARS = new Set(["{{revoganteQual}}", "{{advQual}}"]);

export function resolveRevogacaoText(text, vars) {
  let result = String(text || "");
  for (const [key, val] of Object.entries(vars)) {
    const safeVal = HTML_TRUSTED_VARS.has(key) ? String(val ?? "") : escapeHtml(val);
    result = result.split(key).join(safeVal);
  }
  return result;
}

// Todos os blocos deste documento são ALWAYS — sem condições variáveis.
// Mantido como função para espelhar a assinatura da Declaração/Procuração e
// permitir extensão futura sem quebrar chamadas existentes.
export function isRevogacaoBlockVisible() {
  return true;
}
export function isRevogacaoChildVisible() {
  return true;
}
